/**
 * 応募フォーム設定（form-setting）の Alpine コンポーネント。
 *
 * 重い処理（master の choices 解決・テンプレート項目生成・旧データ救済）は
 * サーバー側(RecruitEdit::loadFormSetting / 既存 CrecruitFormSetting ロジック)で
 * 事前計算し、ここでは plain データに対する単純な配列操作のみ行う。
 * 並び替えはネイティブ HTML5 ドラッグ（DOMを動かさず配列を作り直す）で実装し、
 * Sortable と Alpine x-for の競合を避ける。
 */
document.addEventListener('alpine:init', () => {
    window.Alpine.data('recruitFormSetting', (init = {}) => ({
        entries: init.entries || {},
        entryEnabled: init.entryEnabled || {},
        entryShowSalesNotice: init.entryShowSalesNotice || {},
        masters: init.masters || [],
        templateItems: init.templateItems || {},
        templateDefs: init.templateDefs || [],
        entryDefs: init.entryDefs || [],
        lockedCodes: init.lockedCodes || [],
        alwaysRequiredCodes: init.alwaysRequiredCodes || [],
        dirty: {},

        // 追加モーダル（同時に1つ）
        addOpen: false,
        addEntry: null,
        modalStep: 1,
        selectedIds: [],
        reqMap: {},

        // ドラッグ状態
        dragEntry: null,
        draggingIdx: null,
        overIdx: null,

        markDirty(entryKey) { this.dirty[entryKey] = true; document.dispatchEvent(new Event('recruit:dirty')); },

        // 利用可能マスター（未追加のもの）
        availableFor(entryKey) {
            const used = (this.entries[entryKey]?.items || []).map((it) => Number(it.master_item_id)).filter(Boolean);
            return this.masters.filter((m) => !used.includes(Number(m.id)));
        },
        // 表示項目（システム項目を除く）→ [{origIdx, item}]
        visibleItems(entryKey) {
            const out = [];
            (this.entries[entryKey]?.items || []).forEach((it, idx) => {
                if (!it.is_system) out.push({ origIdx: idx, item: it });
            });
            return out;
        },
        entryEnabledOf(entryKey) { return this.entryEnabled[entryKey] !== false; },
        otherEnabled(entryKey) { return this.entryDefs.some((e) => e.key !== entryKey && this.entryEnabledOf(e.key)); },
        toggleLocked(entryKey) { return this.entryEnabledOf(entryKey) && !this.otherEnabled(entryKey); },

        typeClass(t) { return t === '記述' ? 'text' : (t === '選択' ? 'select' : 'auto'); },

        // テンプレ切替（編集中は破棄確認）
        // 同じテンプレを再クリックした場合もリセット用途として再適用する（旧Livewire版と同等の挙動）
        switchTemplate(entryKey, tplKey) {
            if (!this.templateItems[tplKey] || !this.entries[entryKey]) return;
            if (this.dirty[entryKey] && !confirm('編集中の内容（追加項目・並び順・必須／任意の変更）はすべて破棄されます。テンプレートを切り替えますか？')) {
                return;
            }
            this.entries[entryKey].base_template = tplKey;
            this.entries[entryKey].items = JSON.parse(JSON.stringify(this.templateItems[tplKey]));
            this.dirty[entryKey] = false;
            document.dispatchEvent(new Event('recruit:dirty'));
        },

        setReq(entryKey, idx, val) {
            if (!['必須', '任意'].includes(val)) return;
            const items = this.entries[entryKey]?.items;
            const item = items && items[idx];
            if (!item || item.is_system) return;
            if (this.alwaysRequiredCodes.includes(item.code)) return;
            if (item.req === val) return;
            item.req = val;
            if (val === '任意' && ['email', 'tel'].includes(item.code)) {
                const cp = item.code === 'email' ? 'tel' : 'email';
                const other = items.find((o) => o.code === cp && o.req === '任意');
                if (other) other.req = '必須';
            }
            this.markDirty(entryKey);
        },

        removeItem(entryKey, idx) {
            const items = this.entries[entryKey]?.items;
            const item = items && items[idx];
            if (!item || item.is_system) return;
            if (this.lockedCodes.includes(item.code)) return;
            if (!confirm(`「${item.name}」を削除しますか？`)) return;
            items.splice(idx, 1);
            this.markDirty(entryKey);
        },

        reorder(entryKey, newOrder) {
            const items = this.entries[entryKey]?.items || [];
            const systemItems = [];
            const reorderable = {};
            items.forEach((it, idx) => { if (it.is_system) systemItems.push(it); else reorderable[idx] = it; });
            const rebuilt = [];
            newOrder.forEach((oi) => {
                oi = Number(oi);
                if (reorderable[oi] !== undefined) { rebuilt.push(reorderable[oi]); delete reorderable[oi]; }
            });
            Object.keys(reorderable).forEach((k) => rebuilt.push(reorderable[k]));
            this.entries[entryKey].items = [...systemItems, ...rebuilt];
            this.markDirty(entryKey);
        },

        toggleEnabled(entryKey) {
            if (this.toggleLocked(entryKey)) return;
            this.entryEnabled[entryKey] = !this.entryEnabledOf(entryKey);
            document.dispatchEvent(new Event('recruit:dirty'));
        },
        toggleSalesNotice(entryKey) {
            this.entryShowSalesNotice[entryKey] = !(this.entryShowSalesNotice[entryKey] === true || this.entryShowSalesNotice[entryKey] === 1);
            document.dispatchEvent(new Event('recruit:dirty'));
        },

        // ---- 追加モーダル ----
        openAdd(entryKey) {
            this.addEntry = entryKey;
            this.modalStep = 1;
            this.selectedIds = [];
            this.reqMap = {};
            this.addOpen = true;
        },
        get modalAvailables() { return this.addEntry ? this.availableFor(this.addEntry) : []; },
        get pickedItems() { return this.modalAvailables.filter((m) => this.selectedIds.includes(m.id)); },
        toggleSelect(id) {
            const i = this.selectedIds.indexOf(id);
            if (i >= 0) this.selectedIds.splice(i, 1); else this.selectedIds.push(id);
        },
        proceedToReq() {
            if (this.selectedIds.length === 0) return;
            const next = {};
            this.selectedIds.forEach((id) => { next[id] = this.reqMap[id] || '任意'; });
            this.reqMap = next;
            this.modalStep = 2;
        },
        backToSelect() { this.modalStep = 1; },
        submitAdd() {
            if (!this.addEntry || this.selectedIds.length === 0) return;
            const entryKey = this.addEntry;
            this.selectedIds.forEach((id) => {
                const m = this.masters.find((x) => Number(x.id) === Number(id));
                if (!m) return;
                if (this.entries[entryKey].items.some((it) => Number(it.master_item_id) === Number(id))) return;
                this.entries[entryKey].items.push({
                    master_item_id: m.id, code: m.code, req: this.reqMap[id] || '任意',
                    type: m.input_type, name: m.item_name, choices: m.choices || [],
                    source_master_label: m.source_master_label, is_custom: false, is_system: false,
                });
            });
            this.markDirty(entryKey);
            this.addOpen = false;
        },

        // ---- ネイティブ HTML5 ドラッグ（DOMは動かさず配列を作り直す） ----
        dragStart(ev, entryKey, origIdx) {
            this.dragEntry = entryKey;
            this.draggingIdx = origIdx;
            ev.dataTransfer.effectAllowed = 'move';
            ev.dataTransfer.setData('text/plain', String(origIdx));
        },
        dragOver(ev, entryKey, origIdx) { if (this.dragEntry === entryKey) { ev.preventDefault(); this.overIdx = origIdx; } },
        dragLeave(origIdx) { if (this.overIdx === origIdx) this.overIdx = null; },
        drop(ev, entryKey, targetOrigIdx, $el) {
            ev.preventDefault();
            const fromIdx = this.draggingIdx;
            this.draggingIdx = null; this.overIdx = null;
            if (this.dragEntry !== entryKey) { this.dragEntry = null; return; }
            this.dragEntry = null;
            if (fromIdx === null || fromIdx === targetOrigIdx) return;
            // 現在のDOM順(data-orig-idx)を取得して並び替え
            const rows = Array.from($el.closest('.fs2__items').querySelectorAll('[data-orig-idx]'));
            const order = rows.map((r) => Number(r.dataset.origIdx));
            const fromPos = order.indexOf(fromIdx);
            const toPos = order.indexOf(targetOrigIdx);
            if (fromPos < 0 || toPos < 0) return;
            order.splice(fromPos, 1);
            order.splice(toPos, 0, fromIdx);
            this.reorder(entryKey, order);
        },
        dragEnd() { this.draggingIdx = null; this.overIdx = null; this.dragEntry = null; },
    }));
});
